/**
 * @module services/patient-registry
 * @description UC-REG-315 (Campana, Diego 2026-11-04) — Padrón de pacientes
 *              registrados: consulta paginada con filtros y descarga CSV
 *              (`GET /patients/registry[/export]`). Disponible solo cuando la
 *              API expone `capabilities.patientRegistry`
 *              (`GET /organization/me`); el Dashboard solo muestra u oculta.
 */
import api from "@/services/api";
import { hasCapabilityIn } from "@/services/capabilities";

/** @type {string} Clave de la capacidad del tenant. */
export const CAPABILITY = "patientRegistry";

/** @type {Object<string,string>} Etiquetas de sexo. */
export const GENDER_LABELS = { m: "Masculino", f: "Femenino", x: "X" };

/** @type {Array<{value:string,label:string}>} Opciones del filtro de verificación. */
export const VERIFIED_OPTIONS = [
  { value: "", label: "Verificación: todas" },
  { value: "padron", label: "Verificados por padrón" },
  { value: "none", label: "Sin verificar por padrón" },
];

/**
 * @description Filtros vacíos del padrón.
 * @returns {{search:string,condition:string,registeredFrom:string,registeredTo:string,verified:string}}
 */
export function emptyFilters() {
  return {
    search: "",
    condition: "",
    registeredFrom: "",
    registeredTo: "",
    verified: "",
  };
}

/**
 * @description ¿El tenant tiene el padrón de pacientes?
 * @param {Object|null} organizationContext - `store.state.organizationContext`
 * @returns {boolean}
 */
export function hasPatientRegistryCapability(organizationContext) {
  return hasCapabilityIn(organizationContext, CAPABILITY);
}

/**
 * @description Params de la API a partir de los filtros (omite vacíos).
 * @param {Object} filters - filtros de la vista
 * @param {{limit?:number, offset?:number}} [paging]
 * @returns {Object}
 */
export function buildRegistryParams(filters = {}, paging = {}) {
  const params = {};
  Object.keys(emptyFilters()).forEach((key) => {
    const value = filters[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      params[key] = String(value).trim();
    }
  });
  if (paging.limit) params.limit = paging.limit;
  if (paging.offset) params.offset = paging.offset;
  return params;
}

/**
 * @description Capitaliza cada palabra (nombres guardados en minúsculas).
 * @param {string} s
 * @returns {string}
 */
export function titleCase(s) {
  return String(s || "")
    .split(" ")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/**
 * @description "Apellido, Nombre" de una fila.
 * @param {{firstName:string,lastName:string}} row
 * @returns {string}
 */
export function fullName(row) {
  return [titleCase(row.lastName), titleCase(row.firstName)]
    .filter(Boolean)
    .join(", ");
}

/**
 * @description Fecha `dd/MM/yyyy` (UTC para fechas de nacimiento, que se
 *              guardan a las 00:00Z; '' si no hay).
 * @param {string|Date|null} value
 * @param {boolean} [utc=false]
 * @returns {string}
 */
export function formatDay(value, utc = false) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return utc
    ? `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`
    : `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/**
 * @description Domicilio en una línea.
 * @param {{street?:string,number?:string,city?:string}} address
 * @returns {string}
 */
export function formatAddress(address = {}) {
  const line = [address.street, address.number].filter(Boolean).join(" ");
  return [titleCase(line), titleCase(address.city)].filter(Boolean).join(", ");
}

/**
 * @description Nombre del archivo CSV.
 * @param {Date} [date=new Date()]
 * @returns {string}
 */
export function csvFilename(date = new Date()) {
  return `padron-pacientes-${date.toISOString().slice(0, 10)}.csv`;
}

/**
 * @description Consulta el padrón.
 * @param {Object} params - de `buildRegistryParams`
 * @returns {Promise<{docs:Object[], total:number, limit:number, offset:number}>}
 */
export function list(params = {}) {
  return api.get("/patients/registry", { params }).then((r) => r.data);
}

/**
 * @description Descarga el CSV (Blob) con los mismos filtros.
 * @param {Object} params
 * @returns {Promise<Blob>}
 */
export function exportCsv(params = {}) {
  return api
    .get("/patients/registry/export", { params, responseType: "blob" })
    .then((r) => r.data);
}

export default {
  CAPABILITY,
  GENDER_LABELS,
  VERIFIED_OPTIONS,
  emptyFilters,
  hasPatientRegistryCapability,
  buildRegistryParams,
  titleCase,
  fullName,
  formatDay,
  formatAddress,
  csvFilename,
  list,
  exportCsv,
};
